import { useCallback } from "react";
import { useFusionAuth } from "@fusionauth/react-sdk";
import Cookies from "js-cookie";
import { fusionAuthConfig } from "../config/fusionAuthConfig";

/**
 * How close to its expiry an id token is treated as stale. DS rejects an
 * expired token, and the request still has to travel.
 */
export const ID_TOKEN_EXPIRY_WINDOW_MS = 60_000;

/**
 * The `exp` of a JWT in epoch milliseconds, read WITHOUT verifying it (DS
 * verifies); null when the token is not a readable JWT.
 */
export const idTokenExpiresAt = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const isStale = (token: string, now: number): boolean => {
  const expiresAt = idTokenExpiresAt(token);
  return expiresAt === null || expiresAt < now + ID_TOKEN_EXPIRY_WINDOW_MS;
};

/**
 * Forces the hosted-backend refresh, which sets a new `app.idt` along with the
 * access token. The SDK's `refreshToken()` only calls it when the ACCESS token
 * is about to expire, so an id token that expired first stayed expired.
 */
const forceSessionRefresh = async (): Promise<void> => {
  const url = new URL(
    `/app/refresh/${fusionAuthConfig.clientID}`,
    fusionAuthConfig.serverUrl,
  );
  await fetch(url.toString(), {
    method: "POST",
    headers: { "content-type": "text/plain" },
    credentials: "include",
  });
};

/**
 * The FusionAuth id token cookie the hosted backend sets for ABO (readable
 * from JavaScript, unlike the HttpOnly `app.at` / `app.rt`). DS task 115
 * verifies it (signature, issuer, audience = the ABO application, expiry) to
 * learn which operator is revealing a member password.
 */
export const OPERATOR_TOKEN_COOKIE = "app.idt";

/**
 * The operator's FusionAuth id token, or an empty string when none can be
 * read. Never logged, stored or cached: callers use it for one request.
 */
export const readOperatorToken = (): string => {
  try {
    return Cookies.get(OPERATOR_TOKEN_COOKIE)?.trim() ?? "";
  } catch {
    return "";
  }
};

export interface OperatorTokenHandle {
  /** Whether a token is readable right now (drives the reveal button). */
  hasToken: boolean;
  /**
   * Renews the FusionAuth session through the SDK when the access token is
   * about to expire, and also forces the refresh when the id token itself is
   * expired or about to be (it can expire first), then returns the id token
   * to send on the ONE request that needs it. A failed refresh is not fatal:
   * the current token (or none) is returned and DS answers 401 when it is no
   * longer valid.
   */
  getOperatorToken: () => Promise<string>;
  /** Starts a new FusionAuth sign-in (the answer to a 401 from DS). */
  signInAgain: () => void;
}

/**
 * The signed-in operator's verifiable identity for the password reveal (ABO
 * task 116, ADR-015). The token is read at call time and handed straight to
 * the request; it is never put in React state, the React Query cache, the
 * URL, storage or a log.
 */
export const useOperatorToken = (): OperatorTokenHandle => {
  const { refreshToken, login } = useFusionAuth();

  const getOperatorToken = useCallback(async (): Promise<string> => {
    try {
      await refreshToken();
    } catch {
      // Refresh unavailable (network, expired refresh token): send what we
      // have and let DS decide.
    }
    const token = readOperatorToken();
    if (!isStale(token, Date.now())) {
      return token;
    }
    try {
      await forceSessionRefresh();
    } catch {
      // Same as above: DS answers 401 and the caller offers "Sign in again".
    }
    return readOperatorToken();
  }, [refreshToken]);

  const signInAgain = useCallback(() => login(), [login]);

  return {
    hasToken: readOperatorToken().length > 0,
    getOperatorToken,
    signInAgain,
  };
};
